import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Loader2, Eye, EyeOff, Mail } from 'lucide-react';
import paynovusLogo from '@/assets/paynovus-logo-horizontal-tagline.png';
import { z } from 'zod';

export default function AdminLogin() {
  const savedEmail = localStorage.getItem('admin.rememberEmail') || '';
  const savedPassword = localStorage.getItem('admin.rememberPassword') || '';
  const savedRemember = localStorage.getItem('admin.rememberMe') === 'true';
  const [email, setEmail] = useState(savedEmail);
  const [password, setPassword] = useState(savedPassword);

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [rememberMe, setRememberMe] = useState(savedRemember);
  const { signIn } = useAdminAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setIsLoading(true);

    // Domain restriction — only @paynovus.com or @scalefocus.com addresses allowed
    const normalizedEmail = email.toLowerCase().trim();
    if (!normalizedEmail.endsWith('@paynovus.com') && !normalizedEmail.endsWith('@scalefocus.com')) {
      setError('Access restricted to @paynovus.com or @scalefocus.com email addresses only.');
      setIsLoading(false);
      return;
    }

    try {
      const { error, user: signedInUser } = await signIn(email, password);
      if (error) {
        setError(error.message || 'Invalid credentials');
      } else {
        // Check if user is actually an admin before proceeding
        const { data: adminData } = await supabase
          .from('admin_users')
          .select('id')
          .eq('user_id', signedInUser?.id ?? '')
          .maybeSingle();

        if (!adminData) {
          await supabase.auth.signOut();
          setError('Your account is not registered in the admin portal. Contact a Super Admin for access.');
          setIsLoading(false);
          return;
        }

        // Persist "remember me" preference — email + password, MFA always required
        if (rememberMe) {
          localStorage.setItem('admin.rememberMe', 'true');
          localStorage.setItem('admin.rememberEmail', email);
          localStorage.setItem('admin.rememberPassword', password);
        } else {
          localStorage.removeItem('admin.rememberMe');
          localStorage.removeItem('admin.rememberEmail');
          localStorage.removeItem('admin.rememberPassword');
        }

        const hasTOTP = signedInUser?.factors?.some(
          f => f.status === 'verified' && f.factor_type === 'totp'
        );

        if (!hasTOTP) {
          navigate('/admin/mfa-setup', { replace: true });
        } else {
          navigate('/admin/mfa-challenge', { replace: true });
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError(null);
    setInfo(null);

    const parsed = z.string().trim().email().safeParse(email);
    if (!parsed.success) {
      setError('Please enter your email address above, then click "Forgot password?".');
      return;
    }

    setIsSendingReset(true);
    try {
      const redirectTo = `${window.location.origin}/admin/set-password`;
      const { error: fnErr, data } = await supabase.functions.invoke('admin-password-reset', {
        body: { email: parsed.data, redirectTo },
      });
      if (fnErr) {
        setError(fnErr.message || 'Could not send password reset email. Please try again.');
        return;
      }
      const alreadySent = (data as any)?.already_sent;
      setResetSent(true);
      if (alreadySent) {
        setInfo('A reset link was already sent recently. Please check your inbox and spam folder — the link is valid for 1 hour. If you still haven\'t received it, wait a few minutes and try again.');
      } else {
        setInfo('Password reset email sent! Check your inbox for a secure link to set your new password. The link expires in 1 hour.');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src={paynovusLogo} alt="PayNovus" className="h-12" />
          </div>
          <div className="flex items-center justify-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <CardTitle className="text-2xl">Admin Portal</CardTitle>
          </div>
          <CardDescription>
            Sign in to access the back-office management system
          </CardDescription>
        </CardHeader>
        <CardContent>
          {resetSent ? (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Check your email</h3>
                {info && (
                  <p className="text-sm text-muted-foreground">{info}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  The link in the email will take you directly to the password reset page.
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { setResetSent(false); setInfo(null); }}
              >
                Back to login
              </Button>
              <Button
                variant="ghost"
                className="w-full text-sm"
                disabled={isSendingReset}
                onClick={() => { setResetSent(false); handleForgotPassword(); }}
              >
                {isSendingReset ? 'Sending…' : 'Resend reset link'}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {info && (
                <Alert>
                  <AlertDescription>{info}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@paynovus.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading || isSendingReset}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading || isSendingReset}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowPassword(prev => !prev)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  className="flex items-center gap-2 cursor-pointer select-none"
                  onClick={() => setRememberMe(prev => !prev)}
                >
                  <div className={`h-4 w-4 rounded-sm border flex items-center justify-center ${rememberMe ? 'bg-primary border-primary' : 'border-muted-foreground'}`}>
                    {rememberMe && (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3 text-primary-foreground"><polyline points="20 6 9 17 4 12" /></svg>
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Remember me
                  </span>
                </button>
                <Button
                  type="button"
                  variant="link"
                  className="px-0"
                  onClick={handleForgotPassword}
                  disabled={isLoading || isSendingReset}
                >
                  {isSendingReset ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" />Sending…</> : 'Forgot password?'}
                </Button>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading || isSendingReset}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
